import React, { useState, useEffect } from 'react';
import { Modal, Typography, Spin, Empty, Table, Tag, Button, Card, Row, Col, Statistic, Progress, Divider } from 'antd';
import {
  SendOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  FornecedoresStats,
  TentativaEnvio,
  TentativasEnvioStats
} from '../../../../types/dashboard/fornecedores.types';
import FornecedoresService from '../../../../services/dashboard/fornecedores.service';

const { Title, Text } = Typography;

interface TentativasEnvioModalProps {
  visible: boolean;
  onClose: () => void;
  stats: FornecedoresStats;
  clienteId: string;
}

const TentativasEnvioModal: React.FC<TentativasEnvioModalProps> = ({
  visible,
  onClose,
  stats,
  clienteId
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tentativas, setTentativas] = useState<TentativaEnvio[]>([]);
  const [tentativasStats, setTentativasStats] = useState<TentativasEnvioStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'detalhes'>('stats');

  useEffect(() => {
    const fetchTentativasData = async () => {
      if (!visible || !clienteId) return;

      setLoading(true);
      setError(null);

      try {
        console.log('Buscando dados de tentativas para cliente:', clienteId);
        
        const response = await FornecedoresService.getTentativasEnvio(clienteId);
        
        if (response && response.data) {
          // CORREÇÃO: Filtrar rigorosamente apenas tentativas 1, 2 e 3
          const tentativasFiltradas = (response.data.tentativas || [])
            .filter((tentativa: TentativaEnvio) => tentativa.tentativa >= 1 && tentativa.tentativa <= 3)
            .map((tentativa: TentativaEnvio) => ({
              ...tentativa,
              // Garantir que tentativa seja limitada a 1, 2 ou 3
              tentativa: Math.min(Math.max(tentativa.tentativa, 1), 3) as 1 | 2 | 3,
              // Garantir formatação correta das datas
              dataCadastroFornecedor: tentativa.dataCadastroFornecedor || null,
              dataInicioModulo: tentativa.dataInicioModulo || null,
              dataEnvio: tentativa.dataEnvio || null,
              dataResposta: tentativa.dataResposta || null
            }));

          console.log('Tentativas filtradas (1-3 apenas):', tentativasFiltradas.length);
          
          setTentativas(tentativasFiltradas);
          
          const statsRecalculadas = recalcularEstatisticas(tentativasFiltradas);
          setTentativasStats(statsRecalculadas);
        } else {
          console.warn('Resposta vazia ou inválida da API');
          setTentativas([]);
          setTentativasStats(getStatsVazias());
        }
      } catch (error) {
        console.error('Erro ao buscar tentativas de envio:', error);
        setError('Falha ao carregar dados de tentativas de envio');
        setTentativas([]);
        setTentativasStats(getStatsVazias());
      } finally {
        setLoading(false);
      }
    };

    fetchTentativasData();
  }, [visible, clienteId]);

  // CORREÇÃO: Função para recalcular estatísticas garantindo apenas 3 tentativas
  const recalcularEstatisticas = (tentativasList: TentativaEnvio[]): TentativasEnvioStats => {
    const stats: TentativasEnvioStats = {
      tentativa1: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
      tentativa2: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
      tentativa3: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
      totalGeral: { envios: 0, respondidos: 0, percentualResposta: 0 }
    };

    tentativasList.forEach(tentativa => {
      if (tentativa.tentativa < 1 || tentativa.tentativa > 3) {
        console.warn('Tentativa inválida encontrada:', tentativa.tentativa, 'ignorando...');
        return;
      }

      const statsKey = `tentativa${tentativa.tentativa}` as 'tentativa1' | 'tentativa2' | 'tentativa3';
      
      stats[statsKey].total++;
      stats.totalGeral.envios++;

      if (tentativa.statusResposta === 'respondido' || tentativa.statusResposta === 'em_analise') {
        stats[statsKey].respondidos++;
        stats.totalGeral.respondidos++;
      } else {
        stats[statsKey].naoRespondidos++;
      }
    });

    // Calcular percentuais
    (['tentativa1', 'tentativa2', 'tentativa3'] as const).forEach(key => {
      if (stats[key].total > 0) {
        stats[key].percentualResposta = Math.round((stats[key].respondidos / stats[key].total) * 100);
      }
    });

    if (stats.totalGeral.envios > 0) {
      stats.totalGeral.percentualResposta = Math.round((stats.totalGeral.respondidos / stats.totalGeral.envios) * 100);
    }

    console.log('Estatísticas recalculadas:', stats);
    return stats;
  };

  // Função para retornar estatísticas vazias
  const getStatsVazias = (): TentativasEnvioStats => ({
    tentativa1: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
    tentativa2: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
    tentativa3: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
    totalGeral: { envios: 0, respondidos: 0, percentualResposta: 0 }
  });

  // Colunas com larguras otimizadas para responsividade
  const columns = [
    {
      title: 'Razão Social',
      dataIndex: 'razaoSocial',
      key: 'razaoSocial',
      render: (text: string, record: TentativaEnvio) => (
        <div 
          className="tentativas-cell-wrapper"
          title={`${text || 'N/A'} - Confiança: ${record.confianca || 'não informada'}`}
          style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Text 
            strong 
            className={record.confianca ? `tentativas-confianca-${record.confianca}` : ''}
          >
            {text || 'N/A'}
          </Text>
        </div>
      ),
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => (
        <div 
          className="tentativas-cell-wrapper" 
          title={text}
          style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {text || 'N/A'}
        </div>
      ),
      width: 160,
      ellipsis: true
    },
    {
      title: 'Cadastro',
      dataIndex: 'dataCadastroFornecedor',
      key: 'dataCadastroFornecedor',
      render: (date: string) => (
        <div className="tentativas-cell-center">
          {date ? new Date(date).toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit',
            year: '2-digit'
          }) : 'N/A'}
        </div>
      ),
      width: 75,
      sorter: (a: TentativaEnvio, b: TentativaEnvio) => 
        new Date(a.dataCadastroFornecedor || 0).getTime() - new Date(b.dataCadastroFornecedor || 0).getTime(),
    },
    {
      title: 'Liberação',
      dataIndex: 'dataInicioModulo',
      key: 'dataInicioModulo',
      render: (date: string) => (
        <div className="tentativas-cell-center">
          {date ? new Date(date).toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit',
            year: '2-digit'
          }) : 'N/A'}
        </div>
      ),
      width: 75,
      sorter: (a: TentativaEnvio, b: TentativaEnvio) => 
        new Date(a.dataInicioModulo || 0).getTime() - new Date(b.dataInicioModulo || 0).getTime(),
    },
    {
      title: 'Tentativa',
      dataIndex: 'tentativa',
      key: 'tentativa',
      render: (tentativa: number) => {
        // CORREÇÃO: Garantir que apenas 1, 2, 3 sejam exibidas
        if (tentativa < 1 || tentativa > 3) {
          return <Tag color="default">?</Tag>;
        }
        
        let color = 'default';
        let text = '';
        
        switch (tentativa) {
          case 1:
            color = 'blue';
            text = '1ª Tentativa';
            break;
          case 2:
            color = 'orange';
            text = '2ª Tentativa';
            break;
          case 3:
            color = 'red';
            text = '3ª Tentativa';
            break;
          default:
            text = `${tentativa}ª`;
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 55,
      filters: [
        { text: '1ª Tentativa', value: 1 },
        { text: '2ª Tentativa', value: 2 },
        { text: '3ª Tentativa', value: 3 },
      ],
      onFilter: (value: any, record: TentativaEnvio) => record.tentativa === value,
    },
    {
      title: 'Envio',
      dataIndex: 'dataEnvio',
      key: 'dataEnvio',
      render: (date: string) => (
        <div className="tentativas-cell-center">
          {date ? new Date(date).toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit',
            year: '2-digit'
          }) : 'N/A'}
        </div>
      ),
      width: 75,
      sorter: (a: TentativaEnvio, b: TentativaEnvio) => 
        new Date(a.dataEnvio || 0).getTime() - new Date(b.dataEnvio || 0).getTime(),
    },
    {
      title: 'Status',
      dataIndex: 'statusResposta',
      key: 'statusResposta',
      render: (status: string, record: TentativaEnvio) => {
        let color = 'default';
        let icon = <ClockCircleOutlined />;
        let text = '';
        
        switch (status) {
          case 'respondido':
            color = 'success';
            icon = <CheckOutlined />;
            text = 'Respondido';
            break;
          case 'em_analise':
            color = 'processing';
            icon = <ClockCircleOutlined />;
            text = 'Análisando';
            break;
          case 'nao_respondido':
            color = 'error';
            icon = <ClockCircleOutlined />;
            text = 'Pendente';
            break;
          default:
            text = 'N/A';
        }
        
        return (
          <div className="tentativas-status-cell">
            <Tag color={color} icon={icon} style={{ fontSize: '10px' }}>
              {text}
            </Tag>
            {record.dataResposta && (
              <div style={{ fontSize: '9px', marginTop: '2px', color: '#666' }}>
                {new Date(record.dataResposta).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: '2-digit'
                })}
              </div>
            )}
          </div>
        );
      },
      width: 80,
      filters: [
        { text: 'Respondido', value: 'respondido' },
        { text: 'Em Análise', value: 'em_analise' },
        { text: 'Não Respondido', value: 'nao_respondido' },
      ],
      onFilter: (value: any, record: TentativaEnvio) => record.statusResposta === value,
    },
    {
      title: 'Tempo',
      dataIndex: 'tempoResposta',
      key: 'tempoResposta',
      render: (tempo: number | undefined) => (
        <div className="tentativas-cell-center">
          {tempo === undefined || tempo === null ? '-' : 
           tempo === 0 ? '0d' : `${tempo}d`}
        </div>
      ),
      width: 50,
      sorter: (a: TentativaEnvio, b: TentativaEnvio) => 
        (a.tempoResposta || 0) - (b.tempoResposta || 0),
    },
    {
      title: 'Área',
      dataIndex: 'area',
      key: 'area',
      render: (text: string) => (
        <div 
          className="tentativas-cell-wrapper" 
          title={text}
          style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {text || 'N/A'}
        </div>
      ),
      width: 90,
      ellipsis: true
    }
  ];

  const renderStats = () => {
    if (!tentativasStats) return <Empty description="Estatísticas não disponíveis" />;

    return (
      <div className="tentativas-stats-container">
        {/* Estatísticas Gerais */}
        <Card style={{ marginBottom: '16px' }} size="small">
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Estatísticas Gerais de Tentativas (Limitado a 3 tentativas)
          </Title>
          
          <Row gutter={[16, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total de Envios"
                value={tentativasStats.totalGeral.envios}
                prefix={<SendOutlined />}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total Respondidos"
                value={tentativasStats.totalGeral.respondidos}
                prefix={<CheckOutlined />}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Taxa de Resposta"
                value={tentativasStats.totalGeral.percentualResposta}
                suffix="%"
                precision={1}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
          </Row>
          
          <Progress
            percent={tentativasStats.totalGeral.percentualResposta}
            status="active"
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            size="small"
          />
        </Card>

        {/* Estatísticas por Tentativa */}
        <Row gutter={[8, 8]}>
          {/* 1ª Tentativa */}
          <Col xs={24} md={8}>
            <Card
              title={<Tag color="blue">1ª Tentativa</Tag>}
              size="small"
              className="tentativas-card"
            >
              <div className="tentativas-card-content">
                <Statistic
                  title="Enviados"
                  value={tentativasStats.tentativa1.total}
                  valueStyle={{ fontSize: '14px' }}
                />
                <Statistic
                  title="Respondidos"
                  value={tentativasStats.tentativa1.respondidos}
                  valueStyle={{ color: '#3f8600', fontSize: '14px' }}
                />
                <Statistic
                  title="Não Respondidos"
                  value={tentativasStats.tentativa1.naoRespondidos}
                  valueStyle={{ color: '#cf1322', fontSize: '14px' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Taxa de Resposta:</Text>
                  <Progress
                    percent={tentativasStats.tentativa1.percentualResposta}
                    size="small"
                    strokeColor="#52c41a"
                    showInfo={false}
                  />
                  <Text style={{ fontSize: '12px' }}>{tentativasStats.tentativa1.percentualResposta}%</Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* 2ª Tentativa */}
          <Col xs={24} md={8}>
            <Card
              title={<Tag color="orange">2ª Tentativa</Tag>}
              size="small"
              className="tentativas-card"
            >
              <div className="tentativas-card-content">
                <Statistic
                  title="Enviados"
                  value={tentativasStats.tentativa2.total}
                  valueStyle={{ fontSize: '14px' }}
                />
                <Statistic
                  title="Respondidos"
                  value={tentativasStats.tentativa2.respondidos}
                  valueStyle={{ color: '#3f8600', fontSize: '14px' }}
                />
                <Statistic
                  title="Não Respondidos"
                  value={tentativasStats.tentativa2.naoRespondidos}
                  valueStyle={{ color: '#cf1322', fontSize: '14px' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Taxa de Resposta:</Text>
                  <Progress
                    percent={tentativasStats.tentativa2.percentualResposta}
                    size="small"
                    strokeColor="#faad14"
                    showInfo={false}
                  />
                  <Text style={{ fontSize: '12px' }}>{tentativasStats.tentativa2.percentualResposta}%</Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* 3ª Tentativa */}
          <Col xs={24} md={8}>
            <Card
              title={<Tag color="red">3ª Tentativa</Tag>}
              size="small"
              className="tentativas-card"
            >
              <div className="tentativas-card-content">
                <Statistic
                  title="Enviados"
                  value={tentativasStats.tentativa3.total}
                  valueStyle={{ fontSize: '14px' }}
                />
                <Statistic
                  title="Respondidos"
                  value={tentativasStats.tentativa3.respondidos}
                  valueStyle={{ color: '#3f8600', fontSize: '14px' }}
                />
                <Statistic
                  title="Não Respondidos"
                  value={tentativasStats.tentativa3.naoRespondidos}
                  valueStyle={{ color: '#cf1322', fontSize: '14px' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Taxa de Resposta:</Text>
                  <Progress
                    percent={tentativasStats.tentativa3.percentualResposta}
                    size="small"
                    strokeColor="#ff4d4f"
                    showInfo={false}
                  />
                  <Text style={{ fontSize: '12px' }}>{tentativasStats.tentativa3.percentualResposta}%</Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderDetalhes = () => {
    // Agrupar tentativas por fornecedor para mostrar em sequência
    const tentativasAgrupadas = tentativas.reduce((acc, tentativa) => {
      if (!acc[tentativa.fornecedorId]) {
        acc[tentativa.fornecedorId] = [];
      }
      acc[tentativa.fornecedorId].push(tentativa);
      return acc;
    }, {} as Record<string, TentativaEnvio[]>);

    // Ordenar tentativas dentro de cada fornecedor por número da tentativa
    const tentativasOrdenadas: TentativaEnvio[] = [];
    Object.values(tentativasAgrupadas).forEach(fornecedorTentativas => {
      const ordenadas = fornecedorTentativas
        .filter(t => t.tentativa >= 1 && t.tentativa <= 3) // CORREÇÃO: Filtrar novamente
        .sort((a, b) => a.tentativa - b.tentativa);
      tentativasOrdenadas.push(...ordenadas);
    });

    return (
      <div className="tentativas-detalhes-container">
        <div className="tentativas-detalhes-header">
          <Text type="secondary">
            Exibindo {tentativasOrdenadas.length} tentativa(s) de envio (limitado a 3 tentativas máximo)
          </Text>
          <Text type="secondary">
            Agrupadas por fornecedor
          </Text>
        </div>
        
        <div className="tentativas-table-container">
          <Table
            dataSource={tentativasOrdenadas}
            columns={columns}
            rowKey={(record) => `${record.fornecedorId}-${record.tentativa}`}
            pagination={{ 
              pageSize: 25,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} tentativas`,
              pageSizeOptions: ['10', '25', '50', '100'],
              defaultPageSize: 25,
              responsive: true
            }}
            scroll={{ 
              y: 'calc(65vh - 200px)',
              x: 900
            }}
            size="small"
            rowClassName={(record, index) => {
              const prevRecord = tentativasOrdenadas[index - 1];
              if (!prevRecord || prevRecord.fornecedorId !== record.fornecedorId) {
                return 'tentativas-fornecedor-novo-grupo';
              }
              return '';
            }}
            locale={{
              emptyText: (
                <Empty 
                  description="Nenhuma tentativa de envio encontrada"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ReloadOutlined style={{ marginRight: '8px', fontSize: '20px', color: '#1890ff' }} />
          <span>Tentativas de Envio de Questionários</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="98%"
      style={{ 
        top: 10,
        maxWidth: '1600px'
      }}
      bodyStyle={{
        height: 'calc(100vh - 100px)',
        maxHeight: '90vh',
        overflow: 'hidden',
        padding: '16px'
      }}
      className="tentativas-modal"
    >
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Carregando dados de tentativas de envio...</Text>
          </div>
        </div>
      ) : error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <Text type="danger">{error}</Text>
        </div>
      ) : (
        <div style={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          {/* Navegação por abas */}
          <div style={{ 
            marginBottom: '16px', 
            borderBottom: '1px solid #f0f0f0',
            flexShrink: 0,
            paddingBottom: '8px'
          }}>
            <Button
              type={activeTab === 'stats' ? 'primary' : 'text'}
              onClick={() => setActiveTab('stats')}
              style={{ marginRight: '8px' }}
            >
              Estatísticas
            </Button>
            <Button
              type={activeTab === 'detalhes' ? 'primary' : 'text'}
              onClick={() => setActiveTab('detalhes')}
            >
              Detalhes das Tentativas
            </Button>
          </div>

          {/* Conteúdo da aba ativa */}
          <div style={{ 
            flex: 1,
            overflow: 'hidden',
            minHeight: 0
          }}>
            {activeTab === 'stats' ? renderStats() : renderDetalhes()}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TentativasEnvioModal;